import _ from 'lodash';
import pip from 'point-in-polygon';
import {
  AdminAnswerFieldsFragment,
  AdminQuestionStepFieldsFragment,
  QuestionStepType,
} from '@graphql/admin/generated/graphql.types';
import { convertStringToPointsKeyArray } from './convertStringToPointsKeyArray';
import { roundToPrecision } from './roundToPrecision';

export const calculateScoreForAnswer = (
  step: AdminQuestionStepFieldsFragment,
  answer?: AdminAnswerFieldsFragment | null,
  correction?: AdminAnswerFieldsFragment | null
) => {
  const maxScore = step.score ?? 1;

  // corrections not affected by existing score
  if (step.type === QuestionStepType.Practical) {
    const weights =
      step.answer?.numericlistValue?.filter(
        (v, index) => !!correction?.booleanlistValue?.[index]
      ) ?? [];
    return Math.max(
      0,
      Math.min(
        maxScore,
        roundToPrecision(
          weights.reduce((acc, v) => {
            return acc + (maxScore * v) / 100;
          }, 0),
          1
        )
      )
    );
  }

  // corrections that are affected by existing score
  if (step.type === QuestionStepType.Numeric) {
    if (!_.isNil(answer?.numericValue)) {
      const isRange =
        !!step.answer?.booleanValue &&
        step.answer?.numericlistValue &&
        !!step.answer.numericlistValue.length;

      if (
        (isRange &&
          (answer?.numericValue ?? 0) >=
            (step?.answer?.numericlistValue?.[0] ?? 0) &&
          (answer?.numericValue ?? 0) <=
            (step?.answer?.numericlistValue?.[1] ?? 0)) ||
        answer?.numericValue === step.answer?.numericValue
      ) {
        return maxScore;
      }
    }
    return 0;
  }

  if (step.type === QuestionStepType.MultipleChoice) {
    // this is the total count of answers which should be checked
    const totalAnswersCount = step.answer?.booleanlistValue?.length ?? 0;
    const shouldBeCheckedAnswersCount =
      step.answer?.booleanlistValue?.filter((value) => !!value).length ?? 0;

    // the amount of points awarded for each correctly checked answer
    const pointsPerCorrectAnswer = maxScore / shouldBeCheckedAnswersCount;
    // the amount of points subtracted for each incorrectly checked answer
    const pointsPerIncorrectAnswer = maxScore / totalAnswersCount;
    // "correct" empty answers are ignored in scoring

    const rawScore =
      step.answer?.booleanlistValue?.reduce(
        (score, shouldBeChecked, answerIndex) => {
          // correctly checked
          if (shouldBeChecked && answer?.booleanlistValue?.[answerIndex])
            return score + pointsPerCorrectAnswer;
          // incorrectly checked
          if (!shouldBeChecked && answer?.booleanlistValue?.[answerIndex])
            return score - pointsPerIncorrectAnswer;
          // no points awarded
          return score;
        },
        0
      ) ?? 0;

    return Math.max(0, Math.min(maxScore, roundToPrecision(rawScore, 1)));
  }

  if (step.type === QuestionStepType.Hotzone) {
    const areas = _.compact(
      step.answer?.textlistValue?.map((v) => convertStringToPointsKeyArray(v))
    );
    const points = (answer?.textlistValue?.map((v) =>
      v.split(',').map(parseFloat)
    ) ?? []) as [number, number][];
    const scoreStep = maxScore / areas.length;
    const areasCovered = areas.filter((area) => {
      return points.some((point) => pip(point, area.value));
    });
    const wronglySelectedPoints = points.filter((point) => {
      // if not part of an erea
      return !areas.some((area) => pip(point, area.value));
    });
    const positiveScore = areasCovered.length * scoreStep;
    const negativeScore = wronglySelectedPoints.length * scoreStep;
    return Math.max(
      0,
      Math.min(maxScore, roundToPrecision(positiveScore - negativeScore, 1))
    );
  }

  // @README practical and text do not get scored automatically
  return 0;
};
